/**
 * AcumenActionStrip — ACUMEN-10 RELAY Phase 1
 * 
 * Floating action strip triggered by text selection anywhere in the app.
 * 5 color-coded chips send selected text + session context to Acumen-10.
 * Generated by Qwen3-coder:30b, reviewed + patched by Lucid.
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAcumen } from '../../context/AcumenContext';
import { useTheme } from '../../context/ThemeContext';

const CHIPS = [
  { id: 'comply',   label: 'COMPLY',   color: '#ef4444', prompt: (t) => `Check this text for FAR/VAAR compliance issues, missing regulatory citations, and procurement errors. Be specific. Text: "${t}"` },
  { id: 'research', label: 'RESEARCH', color: '#3b82f6', prompt: (t) => `Provide market research intelligence relevant to this federal procurement text. Include NAICS data, vendor pool insights, and market benchmarks where relevant. Text: "${t}"` },
  { id: 'guide',    label: 'GUIDE',    color: '#10b981', prompt: (t) => `Based on this acquisition text, what comes next in the procurement lifecycle? What is missing or required before proceeding? Cite applicable FAR parts. Text: "${t}"` },
  { id: 'generate', label: 'GENERATE', color: '#f59e0b', prompt: (t) => `Rewrite this in formal federal acquisition language. Add appropriate FAR/VAAR citations, government contracting terminology, and professional tone. Text: "${t}"` },
  { id: 'library',  label: 'LIBRARY',  color: '#8b5cf6', prompt: (t) => `What FAR/VAAR clauses, standard provisions, or acquisition templates are most relevant to this text? List the top 3-5 with brief descriptions. Text: "${t}"` },
];

export default function AcumenActionStrip() {
  const [selectionData, setSelectionData] = useState(null);
  const [activeChip, setActiveChip] = useState(null);
  const [response, setResponse] = useState(null);
  const [loading, setLoading] = useState(false);
  const containerRef = useRef(null);
  const { query_acumen, sessionContext } = useAcumen();
  const { darkMode } = useTheme();

  const bg = darkMode ? '#1a1f2e' : '#ffffff';
  const border = darkMode ? '#2d3748' : '#e5e7eb';
  const textColor = darkMode ? '#e2e8f0' : '#111827';

  const dismiss = useCallback(() => {
    setSelectionData(null);
    setResponse(null);
    setActiveChip(null);
  }, []);

  const handleMouseUp = useCallback((e) => {
    // Don't trigger if clicking inside our own strip
    if (containerRef.current?.contains(e.target)) return;

    // Don't trigger on elements that opt out
    if (e.target.closest('[data-no-acumen]')) return;

    setTimeout(() => {
      const sel = window.getSelection();
      const text = sel?.toString().trim();
      if (!text || text.length < 10) {
        setSelectionData(null);
        setResponse(null);
        return;
      }
      try {
        const range = sel.getRangeAt(0);
        const rect = range.getBoundingClientRect();
        if (rect.width === 0 && rect.height === 0) return;
        setSelectionData({ text, rect });
        setResponse(null);
        setActiveChip(null);
      } catch (_) {}
    }, 10);
  }, []);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Escape') dismiss();
  }, [dismiss]);

  const handleMouseDown = useCallback((e) => {
    if (containerRef.current && !containerRef.current.contains(e.target)) {
      dismiss();
    }
  }, [dismiss]);

  useEffect(() => {
    document.addEventListener('mouseup', handleMouseUp);
    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('mousedown', handleMouseDown);
    return () => {
      document.removeEventListener('mouseup', handleMouseUp);
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('mousedown', handleMouseDown);
    };
  }, [handleMouseUp, handleKeyDown, handleMouseDown]);

  const handleChipClick = async (chip) => {
    if (loading || !selectionData) return;
    setActiveChip(chip.id);
    setLoading(true);
    setResponse(null);
    try {
      const result = await query_acumen(
        chip.prompt(selectionData.text),
        chip.id,
        { selected_text: selectionData.text, source: 'action_strip' }
      );
      setResponse(result);
    } catch (_) {
      setResponse('Acumen is temporarily unavailable.');
    } finally {
      setLoading(false);
    }
  };

  if (!selectionData) return null;

  const { rect } = selectionData;
  const stripTop = rect.top + window.scrollY - 56;
  const stripLeft = rect.left + window.scrollX + rect.width / 2;
  const responseTop = response ? (rect.top + window.scrollY + rect.height + 8) : null;

  return (
    <div ref={containerRef}>
      {/* Action Strip */}
      <div style={{
        position: 'absolute',
        top: stripTop,
        left: stripLeft,
        transform: 'translateX(-50%)',
        display: 'flex',
        gap: 6,
        padding: '7px 10px',
        background: bg,
        border: `1px solid ${border}`,
        borderRadius: 12,
        boxShadow: '0 4px 20px rgba(0,0,0,0.18)',
        zIndex: 9995,
        alignItems: 'center',
        whiteSpace: 'nowrap',
      }}>
        {/* Acumen glyph */}
        <span style={{ color: '#6366f1', fontSize: 14, marginRight: 2 }}>⬡</span>

        {CHIPS.map((chip) => (
          <button
            key={chip.id}
            onClick={() => handleChipClick(chip)}
            disabled={loading}
            title={chip.label}
            style={{
              background: activeChip === chip.id ? chip.color : `${chip.color}22`,
              border: `1.5px solid ${chip.color}`,
              borderRadius: 20,
              padding: '3px 11px',
              fontSize: 11,
              fontWeight: 700,
              color: activeChip === chip.id ? '#fff' : chip.color,
              cursor: loading ? 'wait' : 'pointer',
              transition: 'all 0.15s',
              display: 'flex',
              alignItems: 'center',
              gap: 4,
            }}
          >
            {loading && activeChip === chip.id ? (
              <>
                <span style={{
                  width: 10, height: 10, borderRadius: '50%',
                  border: `2px solid ${chip.color}40`,
                  borderTopColor: chip.color,
                  display: 'inline-block',
                  animation: 'acumen-strip-spin 0.7s linear infinite',
                }} />
                {chip.label}
              </>
            ) : chip.label}
          </button>
        ))}

        {/* Dismiss */}
        <button
          onClick={dismiss}
          style={{
            background: 'none', border: 'none', cursor: 'pointer',
            color: darkMode ? '#64748b' : '#9ca3af',
            fontSize: 14, marginLeft: 2, padding: '0 2px',
          }}
        >✕</button>
      </div>

      {/* Response Panel */}
      {(loading || response) && (
        <div style={{
          position: 'absolute',
          top: responseTop,
          left: stripLeft,
          transform: 'translateX(-50%)',
          width: 380,
          maxHeight: 240,
          overflowY: 'auto',
          background: bg,
          border: `1px solid ${border}`,
          borderRadius: 12,
          boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
          padding: '12px 14px',
          zIndex: 9994,
          fontSize: 13,
          lineHeight: 1.65,
          color: textColor,
        }}>
          {/* Active chip label */}
          {activeChip && (
            <div style={{ marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
              {CHIPS.find(c => c.id === activeChip) && (
                <span style={{
                  background: CHIPS.find(c => c.id === activeChip).color,
                  color: '#fff', fontSize: 10, fontWeight: 700,
                  padding: '2px 8px', borderRadius: 20,
                }}>
                  {CHIPS.find(c => c.id === activeChip).label}
                </span>
              )}
              <span style={{ fontSize: 11, color: darkMode ? '#64748b' : '#9ca3af', fontStyle: 'italic', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 260 }}>
                "{selectionData.text.slice(0, 60)}{selectionData.text.length > 60 ? '…' : ''}"
              </span>
            </div>
          )}

          {loading ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: darkMode ? '#64748b' : '#9ca3af' }}>
              <span style={{
                width: 14, height: 14, borderRadius: '50%',
                border: `2px solid ${(CHIPS.find(c => c.id === activeChip)?.color || '#6366f1')}40`,
                borderTopColor: CHIPS.find(c => c.id === activeChip)?.color || '#6366f1',
                display: 'inline-block',
                animation: 'acumen-strip-spin 0.7s linear infinite',
              }} />
              Acumen is analyzing...
            </div>
          ) : (
            <div style={{ whiteSpace: 'pre-wrap' }}>{response}</div>
          )}
        </div>
      )}

      <style>{`
        @keyframes acumen-strip-spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}
